/**
 * 基准面（Datum Plane）类。
 * 继承 ReferencePlane（抽象基准面），添加可视化显示功能。
 */

import * as THREE from 'three';
import { SceneAuxiliaryObjectRegistry } from '../scene-object';
import type {Subscription} from 'rxjs';
import {ReferencePlane} from './ReferencePlane';
import type {ReferencePlaneHostFaceDescriptor} from './ReferencePlaneHostFace';
import {ReferenceCreationContext} from './ReferenceCreationContext';
import {addObject3DToParent} from '../utils/scene-object/addObject3DToParent';
import {CameraEventName, type CameraStateChangedEventPayload} from '../eventBus';
import {useEvent} from '../hooks';
import {
  cloneReferencePlaneCalculationSource,
  type ReferencePlaneCalculationSource,
} from './reference-plane/ReferencePlaneCalculationSource';


/**
 * 判断对象是否为正式基准面根对象。
 *
 * @param value 待判断对象。
 * @returns 是否为正式基准面根对象。
 */
export function isDatumPlaneObject(value: THREE.Object3D): value is THREE.Group {
  return Reflect.get(value, 'isDatumPlaneObject') === true;
}

/**
 * 判断对象是否为正式基准面网格。
 *
 * @param value 待判断对象。
 * @returns 是否为正式基准面网格对象。
 */
export function isDatumPlaneMesh(value: THREE.Object3D): value is THREE.Mesh {
  return Reflect.get(value, 'isDatumPlaneMesh') === true;
}


/**
 * 从基准面对象层级中读取基准面 ID。
 *
 * @param value 待读取对象。
 * @returns 基准面 ID；对象不属于正式基准面层级时返回 `null`。
 */
export function getDatumPlaneIdFromObject(value: THREE.Object3D): number | null {
  let current: THREE.Object3D | null = value;
  while (current) {
    const planeId = Reflect.get(current, 'datumPlaneId');
    if (Number.isFinite(planeId)) {
      return Number(planeId);
    }
    current = current.parent;
  }
  return null;
}



/**
 * 默认内置基准面稳定身份。
 *
 * 说明：
 * 1. 该枚举只表达系统内置三基准面的不可变槽位身份；
 * 2. 用户重命名只改变显示名称，不改变该身份；
 * 3. 设计树、草图入口和默认面显隐必须依赖该身份，禁止再用显示名反推默认面。
 */
export enum DefaultDatumPlaneKind {
  /** 前视基准面槽位。 */
  Front = 'front',
  /** 上视基准面槽位。 */
  Top = 'top',
  /** 右视基准面槽位。 */
  Right = 'right',
}


/**
 * 基准面构造选项。
 */
export interface DatumPlaneOptions {

  /** 基准面数值 ID；仅历史恢复时传入。 */
  id?: number;

  /** 创建来源上下文。 */
  creationContext: ReferenceCreationContext;

  /** 默认内置基准面稳定身份；用户创建的基准面必须为空。 */
  defaultPlaneKind?: DefaultDatumPlaneKind | null;

  /** 平面原点（世界坐标）。 */
  originWorld?: THREE.Vector3;
  /** 平面法线（单位向量）。 */
  normal?: THREE.Vector3;
  /** 平面 X 轴（单位向量）。 */
  xAxis?: THREE.Vector3;
  /** 平面 Y 轴（单位向量）。 */
  yAxis?: THREE.Vector3;
  /** 基准面名称。 */
  name: string;
  /** 平面面片颜色。 */
  color?: number;
  /** 平面边框颜色。 */
  borderColor?: number;
  /** 平面大小。 */
  size?: number;
  /** 平面显示宽度。 */
  width?: number;
  /** 平面显示高度。 */
  height?: number;
  /** 是否可见。 */
  visible?: boolean;
  /** 不透明度。 */
  opacity?: number;
  /** 是否为临时平面。 */
  isTemporary?: boolean;
  /** 草图宿主面的稳定描述。 */
  hostFace?: ReferencePlaneHostFaceDescriptor | null;
  /** 基准面计算来源快照。 */
  calculationSource?: ReferencePlaneCalculationSource | null;
  /** 用户创建时间戳（毫秒）；历史恢复时传入，新建时不传则由管理器写入当前时间。 */
  createdAt?: number;
}


/**
 * 基准面显示状态快照。
 */
export interface DatumPlaneDisplaySnapshot {
  /** 面片显示颜色。 */
  color: number;
  /** 边框显示颜色。 */
  borderColor: number;
  /** 显示宽度。 */
  width: number;
  /** 显示高度。 */
  height: number;
  /** 是否可见。 */
  visible: boolean;
  /** 不透明度。 */
  opacity: number;
}



/** 基准面显示选项默认值。尺寸缺省值 200 场景米更改为 0.2（200 mm）。 */
const DEFAULT_DISPLAY: Required<Pick<DatumPlaneOptions, 'color' | 'borderColor' | 'size' | 'visible' | 'opacity'>> = {
  color: 0x6dc5f6,
  borderColor: 0x4a9eff,
  size: 0.2,
  visible: true,
  opacity: 0.3,
};



/** 基准面调试包围盒颜色。 */
const DATUM_PLANE_DEBUG_BOX_COLOR = 0xff00ff;


/** 基准面标签字体大小；按上一版 42px 缩小为一半。 */
const DATUM_PLANE_LABEL_FONT_SIZE_PX = 21;
/** 标签文字到纹理边缘的水平留白。 */
const DATUM_PLANE_LABEL_HORIZONTAL_PADDING_PX = 5;
/** 标签文字到纹理边缘的垂直留白。 */
const DATUM_PLANE_LABEL_VERTICAL_PADDING_PX = 3;
/** 标签外框与基准面左、上边界之间的屏幕留白。 */
const DATUM_PLANE_LABEL_EDGE_MARGIN_PX = 12;
/** Canvas 超采样倍率，降低倾斜视角下的文字锯齿与模糊。 */
const DATUM_PLANE_LABEL_TEXTURE_SCALE = 4;
/** Canvas 无法测量文字时使用的单字符宽度估算倍率。 */
const DATUM_PLANE_LABEL_FALLBACK_CHARACTER_WIDTH_RATIO = 0.9;
/** 未收到相机事件时，按基准面短边对应的预估屏幕像素数计算初始标签尺寸。 */
const DATUM_PLANE_LABEL_FALLBACK_PLANE_PIXELS = 500;
/** 标签与基准面共面时使用的最小局部 Z 偏移，防止深度闪烁。 */
const DATUM_PLANE_LABEL_MIN_Z_OFFSET = 1e-7;
/** 标签字体栈。 */
const DATUM_PLANE_LABEL_FONT_FAMILY = '"Microsoft YaHei", "PingFang SC", sans-serif';



/**
 * 基准面。
 * 继承 ReferencePlane 拥有坐标系、局部-世界变换等数学能力，
 * 本类仅扩展可视化网格、标签与不透明度等显示功能。
 */
export class DatumPlane extends ReferencePlane {
  /** CAD 事件总线操作集合。 */
  private readonly eventBus = useEvent();


  /**
   * 基准面调试开关。
   *
   * 说明：
   * 1. `showBoundingBox` 仅用于排查基准面可视面片与拾取范围；
   * 2. 调试关闭时不会创建 helper，避免正式运行期额外对象进入场景树；
   * 3. 调试 helper 会禁用自身 raycast，防止调试对象污染 hover / click 拾取链路。
   */
  public static readonly debug: {
    /** 是否显示正式基准面的调试包围盒。 */
    showBoundingBox: boolean;
  } = {
    showBoundingBox: false,
  };


  /** 是否为临时平面。 */
  public readonly isTemporary: boolean;
  /** 创建来源上下文。 */
  public readonly creationContext: ReferenceCreationContext;

  /** 默认内置基准面稳定身份；用户创建的基准面为 null。 */
  public readonly defaultPlaneKind: DefaultDatumPlaneKind | null;

  /** 基准面计算来源快照。 */
  private readonly calculationSource: ReferencePlaneCalculationSource | null;
  /** 显式显示原点。 */
  private readonly displayOriginWorld: THREE.Vector3;


  /** 基准面场景根对象。 */
  private readonly planeGroup: THREE.Group;
  /** 基准面边框默认材质。 */
  private readonly planeEdgesMaterial: THREE.LineBasicMaterial;
  /** 基准面边框对象。 */
  private readonly planeEdges: THREE.LineSegments;

  /** 可视化网格。 */
  private readonly planeMesh: THREE.Mesh;
  /** 基准面名称的画布纹理 Mesh。 */
  private readonly label: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  /** 当前标签画布纹理。 */
  private labelTexture!: THREE.CanvasTexture;
  /** 当前标签几何的宽高比。 */
  private labelAspectRatio: number = 1;
  /** 当前标签纹理按 CSS 像素折算的显示高度。 */
  private labelDisplayHeightPx: number = DATUM_PLANE_LABEL_FONT_SIZE_PX
      + DATUM_PLANE_LABEL_VERTICAL_PADDING_PX * 2;
  /** 最近一次收到的相机状态，用于基准面改名或改尺寸后立即重新排版。 */
  private lastCameraState: CameraStateChangedEventPayload | null = null;
  /** 相机状态变化事件订阅。 */
  private readonly cameraStateChangedSubscription: Subscription;
  /** 计算透视标签缩放时复用的世界坐标缓存。 */
  private readonly labelWorldPosition = new THREE.Vector3();
  /** 计算透视标签缩放时复用的相机世界坐标缓存。 */
  private readonly cameraWorldPosition = new THREE.Vector3();
  /** 计算透视标签缩放时复用的相机视线方向缓存。 */
  private readonly cameraWorldDirection = new THREE.Vector3();

  /** 调试包围盒 helper。 */
  private debugBoxHelper: THREE.Box3Helper | null;

  /** 显示选项。 */
  private displayOpts: Required<Pick<DatumPlaneOptions, 'color' | 'borderColor' | 'visible' | 'opacity'>> & {
    width: number;
    height: number;
  };

  /**
   * 构造基准面。
   * @param options 构造选项。
   */
  constructor(options: DatumPlaneOptions) {
    // 解析平面数学参数
    const normal = (options.normal ?? new THREE.Vector3(0, 0, 1)).clone().normalize();
    const origin = options.originWorld ?? new THREE.Vector3(0, 0, 0);
    const constant = -normal.dot(origin);
    super(options.name, normal, constant, {
      xAxis: options.xAxis,
      yAxis: options.yAxis,
    }, options.hostFace ?? null, options.id);
    this.isTemporary = options.isTemporary ?? false;
    this.creationContext = options.creationContext;

    // 默认面身份只允许系统内置对象持有，避免用户基准面误占默认槽位。
    this.defaultPlaneKind = options.creationContext === ReferenceCreationContext.DefaultBuiltIn
      ? options.defaultPlaneKind ?? null
      : null;

    this.calculationSource = cloneReferencePlaneCalculationSource(options.calculationSource);

    /**
     * DatumPlane 的可视网格中心必须直接使用构造时传入的 originWorld。
     *
     * 说明：
     * 1. 参考平面数学层只要求“平面上的一点 + 法向”即可定义平面；
     * 2. 但 UI 预览里的面片中心是求解结果中的 `frame.origin`；
     * 3. 若网格位置退回到 `coplanarPoint()` 计算的任意共面点，正式基准面会相对预览发生平移。
     */
    this.displayOriginWorld = origin.clone();


    // 显示选项
    const resolvedDisplayColor = options.color ?? DEFAULT_DISPLAY.color;
    this.displayOpts = {
      color: resolvedDisplayColor,
      borderColor: options.borderColor ?? DEFAULT_DISPLAY.borderColor,
      width: options.width ?? options.size ?? DEFAULT_DISPLAY.size,
      height: options.height ?? options.size ?? DEFAULT_DISPLAY.size,
      visible: options.visible ?? DEFAULT_DISPLAY.visible,
      opacity: options.opacity ?? DEFAULT_DISPLAY.opacity,
    };


    // 创建根对象，正式基准面后续统一以 group 参与场景挂载、选择和显隐。
    this.planeGroup = this.createPlaneGroup();


    // 创建可视化网格
    this.planeMesh = this.createPlaneMesh();
    this.planeEdgesMaterial = this.createPlaneEdgesMaterial();
    this.planeEdges = this.createPlaneEdges();
    this.planeGroup.add(this.planeMesh);
    this.planeGroup.add(this.planeEdges);

    // 创建标签
    this.label = this.createLabel();
    this.planeGroup.add(this.label);
    this.cameraStateChangedSubscription = this.eventBus.add(
        CameraEventName.StateChanged,
        this.handleCameraStateChangedEvent,
    );

    this.debugBoxHelper = this.createDebugBoxHelper();

  }

  // ======================== 显示名称访问器 ========================

  /**
   * 获取基准面名称。
   */
  get name(): string {
    return this.displayName;
  }

  /**
   * 设置基准面名称。
   */
  set name(value: string) {
    this.displayName = value;
  }

  // ======================== 可视化方法 ========================


  /**
   * 创建基准面场景根对象。
   *
   * @returns 基准面根对象。
   */
  private createPlaneGroup(): THREE.Group {
    const group = new THREE.Group();
    group.name = this.displayName;
    group.position.copy(this.displayOriginWorld);
    this.updateGroupOrientation(group);
    group.visible = this.displayOpts.visible;
    Reflect.set(group, 'isDatumPlaneObject', true);
    Reflect.set(group, 'datumPlaneId', this.id);
    Reflect.set(group, 'datumPlaneCreationContext', this.creationContext);
    Reflect.set(group, 'datumPlaneDefaultKind', this.defaultPlaneKind);
    return group;
  }


  /**
   * 创建平面网格。
   */
  private createPlaneMesh(): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(this.displayOpts.width, this.displayOpts.height);
    const material = new THREE.MeshBasicMaterial({
      color: this.displayOpts.color,
      transparent: true,
      opacity: this.displayOpts.opacity,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(geometry, material);

    Reflect.set(mesh, 'isDatumPlaneMesh', true);
    Reflect.set(mesh, 'datumPlaneId', this.id);
    Reflect.set(mesh, 'datumPlaneCreationContext', this.creationContext);
    Reflect.set(mesh, 'datumPlaneDefaultKind', this.defaultPlaneKind);

    mesh.name = this.displayName;

    mesh.renderOrder = -1;
    return mesh;
  }


  /**
   * 创建基准面边框对象。
   *
   * @returns 基准面边框对象。
   */
  private createPlaneEdges(): THREE.LineSegments {
    const edgesGeometry = new THREE.EdgesGeometry(this.planeMesh.geometry);
    const edges = new THREE.LineSegments(edgesGeometry, this.planeEdgesMaterial);
    Reflect.set(edges, 'datumPlaneId', this.id);
    Reflect.set(edges, 'datumPlaneCreationContext', this.creationContext);
    Reflect.set(edges, 'datumPlaneDefaultKind', this.defaultPlaneKind);
    edges.name = `${this.displayName}-edges`;
    return edges;
  }

  /**
   * 创建基准面边框默认材质。
   *
   * @returns 基准面边框默认材质。
   */
  private createPlaneEdgesMaterial(): THREE.LineBasicMaterial {
    return new THREE.LineBasicMaterial({
      color: this.displayOpts.borderColor,
      transparent: true,
      opacity: 0.8,
      linewidth: 2,
    });
  }

  /**
   * 更新基准面根对象方向。
   *
   * @param group 根对象。
   */
  private updateGroupOrientation(group: THREE.Group): void {
    const rotationMatrix = new THREE.Matrix4().makeBasis(
      this.xAxis.clone().normalize(),
      this.yAxis.clone().normalize(),
      this.normal.clone().normalize(),
    );
    group.quaternion.setFromRotationMatrix(rotationMatrix);
  }


  /**
   * 创建基准面名称标签。
   *
   * 标签不是 DOM 文本：名称先绘制到高清 Canvas，再作为纹理贴到基准面内的小平面上。
   * MeshBasicMaterial 不参与场景光照计算，因此文字始终保持黑色。
   */
  private createLabel(): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
    const {texture, aspectRatio, displayHeightPx} = this.createLabelTexture(this.displayName);
    this.labelTexture = texture;
    this.labelAspectRatio = aspectRatio;
    this.labelDisplayHeightPx = displayHeightPx;

    const geometry = new THREE.PlaneGeometry(aspectRatio, 1);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: 0x000000,
      transparent: true,
      alphaTest: 0.01,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
    material.toneMapped = false;

    const label = new THREE.Mesh(geometry, material);
    // 标签不是基准面业务对象，标签重建不能使组件身份缓存失效。
    SceneAuxiliaryObjectRegistry.markRoot(label);
    label.name = `${this.displayName}-label`;
    label.renderOrder = 1;
    label.userData.isDatumPlaneLabel = true;
    label.userData.skipSelection = true;
    // 透明 Canvas 的空白区域不能扩大基准面的射线命中范围。
    label.raycast = (): void => {
    };
    Reflect.set(label, 'datumPlaneId', this.id);
    Reflect.set(label, 'datumPlaneCreationContext', this.creationContext);
    Reflect.set(label, 'datumPlaneDefaultKind', this.defaultPlaneKind);

    this.updateLabelTransform(label);
    return label;
  }

  /**
   * 创建高分辨率透明文字纹理。
   *
   * @param text 标签文字。
   * @returns 画布纹理及其宽高比。
   */
  private createLabelTexture(text: string): {
    texture: THREE.CanvasTexture;
    aspectRatio: number;
    displayHeightPx: number;
  } {
    const canvas = document.createElement('canvas');
    const textureFontSize = DATUM_PLANE_LABEL_FONT_SIZE_PX * DATUM_PLANE_LABEL_TEXTURE_SCALE;
    const horizontalPadding = DATUM_PLANE_LABEL_HORIZONTAL_PADDING_PX * DATUM_PLANE_LABEL_TEXTURE_SCALE;
    const verticalPadding = DATUM_PLANE_LABEL_VERTICAL_PADDING_PX * DATUM_PLANE_LABEL_TEXTURE_SCALE;
    const fallbackCharacterCount = Math.max(1, Array.from(text).length);
    let measuredTextWidth = fallbackCharacterCount
        * textureFontSize
        * DATUM_PLANE_LABEL_FALLBACK_CHARACTER_WIDTH_RATIO;
    let measuredTextAscent = textureFontSize * 0.8;
    let measuredTextDescent = textureFontSize * 0.2;
    let context: CanvasRenderingContext2D | null = null;

    if (
        typeof globalThis.CanvasRenderingContext2D !== 'undefined'
        && typeof canvas.getContext === 'function'
    ) {
      try {
        context = canvas.getContext('2d');
        if (context) {
          context.font = `600 ${textureFontSize}px ${DATUM_PLANE_LABEL_FONT_FAMILY}`;
          const metrics = context.measureText(text);
          measuredTextWidth = Math.max(1, metrics.width);
          if (metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent > 0) {
            measuredTextAscent = metrics.actualBoundingBoxAscent;
            measuredTextDescent = metrics.actualBoundingBoxDescent;
          }
        }
      } catch {
        // 无 Canvas2D 的测试或服务端环境沿用字符宽度估算并创建空纹理。
        context = null;
      }
    }

    canvas.width = Math.max(1, Math.ceil(measuredTextWidth + horizontalPadding * 2));
    canvas.height = Math.max(1, Math.ceil(measuredTextAscent + measuredTextDescent + verticalPadding * 2));

    if (context) {
      // 修改 canvas 尺寸会重置绘图状态，必须在最终尺寸确定后重新设置字体与颜色。
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.font = `600 ${textureFontSize}px ${DATUM_PLANE_LABEL_FONT_FAMILY}`;
      context.textAlign = 'left';
      context.textBaseline = 'alphabetic';
      context.fillStyle = '#000000';
      context.fillText(text, horizontalPadding, verticalPadding + measuredTextAscent);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = 4;
    texture.needsUpdate = true;

    return {
      texture,
      aspectRatio: canvas.width / canvas.height,
      displayHeightPx: canvas.height / DATUM_PLANE_LABEL_TEXTURE_SCALE,
    };
  }

  /**
   * 名称变化后重建 Canvas 纹理和与纹理比例一致的几何体。
   *
   * @param text 新标签文字。
   */
  private updateLabelText(text: string): void {
    const previousTexture = this.labelTexture;
    const previousGeometry = this.label.geometry;
    const {texture, aspectRatio, displayHeightPx} = this.createLabelTexture(text);

    this.labelTexture = texture;
    this.labelAspectRatio = aspectRatio;
    this.labelDisplayHeightPx = displayHeightPx;
    this.label.geometry = new THREE.PlaneGeometry(aspectRatio, 1);
    this.label.material.map = texture;
    this.label.material.needsUpdate = true;
    this.label.name = `${text}-label`;
    previousTexture.dispose();
    previousGeometry.dispose();
    this.updateLabelTransform();
  }

  /**
   * 计算标签所在位置对应的每 CSS 像素世界尺寸。
   *
   * @param state 当前相机状态。
   * @returns 每像素对应的世界尺寸；相机参数不可用时返回 null。
   */
  private resolveWorldUnitsPerPixel(state: CameraStateChangedEventPayload): number | null {
    const viewportHeight = state.viewportHeight;
    if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
      return null;
    }

    const camera = state.camera;
    if (camera instanceof THREE.OrthographicCamera) {
      const visibleHeight = Math.abs(camera.top - camera.bottom) / Math.max(camera.zoom, Number.EPSILON);
      return visibleHeight / viewportHeight;
    }

    if (camera instanceof THREE.PerspectiveCamera) {
      // 左上角锚点比平面中心更接近标签实际位置，斜视和近距离观察时深度换算更准确。
      this.labelWorldPosition.set(-this.displayOpts.width / 2, this.displayOpts.height / 2, 0);
      this.planeGroup.localToWorld(this.labelWorldPosition);
      camera.getWorldPosition(this.cameraWorldPosition);
      camera.getWorldDirection(this.cameraWorldDirection);
      const depth = Math.abs(
          this.labelWorldPosition
              .sub(this.cameraWorldPosition)
              .dot(this.cameraWorldDirection),
      );
      const effectiveFovRadians = THREE.MathUtils.degToRad(camera.getEffectiveFOV());
      const visibleHeight = 2 * Math.max(depth, camera.near) * Math.tan(effectiveFovRadians / 2);
      return visibleHeight / viewportHeight;
    }

    return null;
  }

  /**
   * 更新标签在基准面局部坐标中的尺寸和左上角位置。
   *
   * 21px 字号是上一版 42px 的一半；标签和边界之间保留 12px 间距。当视口缩放时，
   * 相机事件会重新换算世界尺寸，并限制标签不越出当前基准面。
   *
   * @param label 需要更新的标签，创建阶段可显式传入。
   */
  private updateLabelTransform(
      label: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> = this.label,
  ): void {
    const planeWidth = this.displayOpts.width;
    const planeHeight = this.displayOpts.height;
    const fallbackWorldUnitsPerPixel = Math.min(planeWidth, planeHeight)
        / DATUM_PLANE_LABEL_FALLBACK_PLANE_PIXELS;
    const worldUnitsPerPixel = this.lastCameraState
        ? this.resolveWorldUnitsPerPixel(this.lastCameraState) ?? fallbackWorldUnitsPerPixel
        : fallbackWorldUnitsPerPixel;
    const requestedMargin = DATUM_PLANE_LABEL_EDGE_MARGIN_PX * worldUnitsPerPixel;
    const margin = Math.min(requestedMargin, Math.min(planeWidth, planeHeight) * 0.08);
    const availableWidth = Math.max(Number.EPSILON, planeWidth - margin * 2);
    const availableHeight = Math.max(Number.EPSILON, planeHeight - margin * 2);
    const desiredLabelHeight = this.labelDisplayHeightPx * worldUnitsPerPixel;
    const labelHeight = Math.min(
        desiredLabelHeight,
        availableHeight,
        availableWidth / this.labelAspectRatio,
    );
    const labelWidth = labelHeight * this.labelAspectRatio;
    const zOffset = Math.max(
        DATUM_PLANE_LABEL_MIN_Z_OFFSET,
        Math.min(planeWidth, planeHeight) * 1e-5,
    );

    label.scale.set(labelHeight, labelHeight, 1);
    label.position.set(
        -planeWidth / 2 + margin + labelWidth / 2,
        planeHeight / 2 - margin - labelHeight / 2,
        zOffset,
    );
  }

  /**
   * 根据相机状态事件同步标签的屏幕尺寸和边距。
   *
   * @param payload 相机状态变化事件载荷。
   */
  private readonly handleCameraStateChanged = (payload: CameraStateChangedEventPayload): void => {
    this.lastCameraState = payload;
    if (!this.displayOpts.visible) {
      return;
    }
    this.updateLabelTransform();
  };

  /** 处理事件总线的弱类型载荷。 */
  private readonly handleCameraStateChangedEvent = (payload: unknown): void => {
    this.handleCameraStateChanged(payload as CameraStateChangedEventPayload);
  };


  /**
   * 判断显示尺寸是否为有效正数。
   *
   * @param value 待校验尺寸。
   * @returns 尺寸可用于 Three.js 几何体时返回 true。
   */
  private static isValidDisplaySize(value: number | undefined): value is number {
    return Number.isFinite(value) && Number(value) > 0;
  }

  /**
   * 更新基准面的可视几何尺寸。
   *
   * @param width 新显示宽度。
   * @param height 新显示高度。
   */
  private updateDisplaySize(width: number | undefined, height: number | undefined): void {
    if (!DatumPlane.isValidDisplaySize(width) || !DatumPlane.isValidDisplaySize(height)) {
      return;
    }
    if (this.displayOpts.width === width && this.displayOpts.height === height) {
      return;
    }

    const previousPlaneGeometry = this.planeMesh.geometry;
    const previousEdgesGeometry = this.planeEdges.geometry;
    const nextPlaneGeometry = new THREE.PlaneGeometry(width, height);
    const nextEdgesGeometry = new THREE.EdgesGeometry(nextPlaneGeometry);

    this.displayOpts.width = width;
    this.displayOpts.height = height;
    this.planeMesh.geometry = nextPlaneGeometry;
    this.planeEdges.geometry = nextEdgesGeometry;
    previousPlaneGeometry.dispose();
    previousEdgesGeometry.dispose();
    this.updateLabelTransform();

    // 调试包围盒依赖平面几何局部包围盒，尺寸变化后需要重建并释放旧 helper。
    this.disposeDebugBoxHelper();
    this.debugBoxHelper = this.createDebugBoxHelper();
  }


  /**
   * 创建基准面调试包围盒。
   *
   * 说明：
   * 1. 包围盒使用基准面几何的局部包围盒，挂在 `planeGroup` 下后会自动跟随基准面的位移和旋转；
   * 2. 包围盒只表达正式面片的可视范围，不参与业务拾取；
   * 3. 调试关闭时返回 `null`，避免正式运行期创建无用资源。
   *
   * @returns 调试包围盒 helper；调试关闭时返回 `null`。
   */
  private createDebugBoxHelper(): THREE.Box3Helper | null {
    if (!DatumPlane.debug.showBoundingBox) {
      return null;
    }

    this.planeMesh.geometry.computeBoundingBox();
    const boundingBox = this.planeMesh.geometry.boundingBox?.clone() ?? null;
    if (!boundingBox) {
      return null;
    }

    const helper = new THREE.Box3Helper(boundingBox, DATUM_PLANE_DEBUG_BOX_COLOR);
    SceneAuxiliaryObjectRegistry.markRoot(helper);
    helper.name = `${this.displayName}-debug-bounding-box`;
    helper.userData.skipSelection = true;
    helper.userData.isDatumPlaneDebugBox = true;
    /**
     * 调试包围盒不能参与射线命中。
     * 否则基准面拾取链路会把子对象命中归一化回基准面，干扰 hover 问题排查。
     */
    helper.raycast = (): void => {};
    this.planeGroup.add(helper);

    return helper;
  }

  /**
   * 释放基准面调试包围盒。
   */
  private disposeDebugBoxHelper(): void {
    if (!this.debugBoxHelper) {
      return;
    }

    if (this.debugBoxHelper.parent) {
      this.debugBoxHelper.parent.remove(this.debugBoxHelper);
    }
    this.debugBoxHelper.geometry.dispose();
    const material = this.debugBoxHelper.material;
    if (Array.isArray(material)) {
      material.forEach((item) => item.dispose());
      this.debugBoxHelper = null;
      return;
    }
    material.dispose();
    this.debugBoxHelper = null;
  }


  /**
   * 添加到场景。
   * @param scene 场景对象。
   */
  addToScene(scene: THREE.Scene): void {
    addObject3DToParent(this.planeGroup, scene);
    this.updateLabelTransform();
  }

  /**
   * 从场景移除。
   * @param scene 场景对象。
   */
  removeFromScene(scene: THREE.Scene): void {
    scene.remove(this.planeGroup);
  }

  /**
   * 获取可视化网格对象。
   */
  getMesh(): THREE.Mesh {
    return this.planeMesh;
  }


  /**
   * 获取基准面场景根对象。
   *
   * @returns 基准面场景根对象。
   */
  getObject3D(): THREE.Group {
    return this.planeGroup;
  }

  /**
   * 获取基准面边框对象。
   *
   * @returns 基准面边框对象。
   */
  getEdges(): THREE.LineSegments {
    return this.planeEdges;
  }

  /**
   * 获取基准面名称纹理 Mesh。
   *
   * @returns 基准面名称标签对象。
   */
  getLabel(): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
    return this.label;
  }


  /**
   * 获取当前基准面原点（世界坐标）。
   *
   * @returns 显式显示原点副本。
   */
  public override get originWorld(): THREE.Vector3 {
    return this.displayOriginWorld.clone();
  }

  /**
   * 将当前基准面原点写入目标向量。
   *
   * @param out 输出世界坐标。
   * @returns 当前基准面原点。
   */
  public override copyOriginWorldTo(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.displayOriginWorld);
  }


  /**
   * 读取基准面计算来源快照。
   *
   * @returns 计算来源副本；没有来源时返回 `null`。
   */
  public getCalculationSource(): ReferencePlaneCalculationSource | null {
    return cloneReferencePlaneCalculationSource(this.calculationSource);
  }


  /**
   * 读取基准面显示状态快照。
   *
   * @returns 显示状态快照。
   */
  public getDisplaySnapshot(): DatumPlaneDisplaySnapshot {
    return {
      color: this.displayOpts.color,
      borderColor: this.displayOpts.borderColor,
      width: this.displayOpts.width,
      height: this.displayOpts.height,
      visible: this.displayOpts.visible,
      opacity: this.displayOpts.opacity,
    };
  }


  /**
   * 设置基准面面片显示颜色。
   *
   * 设计说明：
   * 1. 面片颜色和边框颜色在调试时需要分别控制，因此该方法只更新面片材质；
   * 2. 调试面板、历史恢复或后续属性面板都应通过该方法改面片色，避免绕过显示快照；
   * 3. 这里限定为 24 位 RGB 整数，防止非法值进入 Three.js 材质并造成难定位的显示异常。
   *
   * @param color 24 位 RGB 颜色值。
   */
  public setColor(color: number): void {
    DatumPlane.assertDisplayColor(color, '设置基准面面片颜色');

    // 面片颜色独立于边框颜色，方便调试半透明叠色和边界识别问题。
    this.displayOpts.color = color;
    const material = this.planeMesh.material as THREE.MeshBasicMaterial;
    material.color.setHex(color);
  }

  /**
   * 设置基准面边框显示颜色。
   *
   * 设计说明：
   * 1. 边框颜色用于判断基准面轮廓，在半透明面片重叠时必须可独立调试；
   * 2. 该方法只更新边框材质和显示快照，不改变面片材质；
   * 3. 交互 hover / selected 会临时替换边框材质，退出交互后仍会恢复到这里维护的默认边框颜色。
   *
   * @param color 24 位 RGB 颜色值。
   */
  public setBorderColor(color: number): void {
    DatumPlane.assertDisplayColor(color, '设置基准面边框颜色');

    // 边框材质单独维护颜色，避免调试边界颜色时污染面片透明叠色判断。
    this.displayOpts.borderColor = color;
    this.planeEdgesMaterial.color.setHex(color);
  }

  /**
   * 断言显示颜色是合法的 24 位 RGB 整数。
   *
   * @param color 待校验颜色。
   * @param actionName 当前动作名称，用于错误定位。
   */
  private static assertDisplayColor(color: number, actionName: string): void {
    if (!Number.isInteger(color) || color < 0 || color > 0xffffff) {
      throw new Error(`${actionName}失败：颜色必须是 0x000000 到 0xffffff 之间的整数，当前值为 ${color}。`);
    }
  }


  /**
   * 设置可见性。
   * @param visible 是否可见。
   */
  setVisible(visible: boolean): void {
    this.displayOpts.visible = visible;
    this.planeGroup.visible = visible;
    if (visible) {
      this.updateLabelTransform();
    }
  }

  /**
   * 设置名称。
   * @param newName 新名称。
   */
  setName(newName: string): void {
    this.displayName = newName;
    this.planeGroup.name = newName;
    this.planeMesh.name = newName;
    this.planeEdges.name = `${newName}-edges`;
    this.updateLabelText(newName);
  }

  /**
   * 获取可见性。
   */
  isVisible(): boolean {
    return this.displayOpts.visible;
  }

  /**
   * 设置不透明度。
   * @param opacity 不透明度。
   */
  setOpacity(opacity: number): void {
    this.displayOpts.opacity = opacity;
    const material = this.planeMesh.material as THREE.MeshBasicMaterial;
    material.opacity = opacity;
  }

  /**
   * 获取不透明度。
   */
  getOpacity(): number {
    return this.displayOpts.opacity;
  }

  /**
   * 根据求解结果更新基准面坐标系。
   *
   * @param input 新的基准面坐标系。
   */
  public updateFrame(input: {
    /** 平面原点（世界坐标）。 */
    originWorld: THREE.Vector3;
    /** 平面法向。 */
    normal: THREE.Vector3;
    /** 平面 X 轴。 */
    xAxis: THREE.Vector3;
    /** 平面 Y 轴。 */
    yAxis: THREE.Vector3;
    /** 平面显示宽度。 */
    width?: number;
    /** 平面显示高度。 */
    height?: number;
  }): void {
    this.updateReferenceFrame(input.originWorld, input.normal, {
      xAxis: input.xAxis,
      yAxis: input.yAxis,
    });
    this.displayOriginWorld.copy(input.originWorld);
    this.planeGroup.position.copy(input.originWorld);
    this.updateGroupOrientation(this.planeGroup);
    this.updateDisplaySize(input.width, input.height);
    this.updateLabelTransform();
  }


  /**
   * 清理资源。
   */
  dispose(): void {
    this.cameraStateChangedSubscription.unsubscribe();

    this.disposeDebugBoxHelper();

    this.planeMesh.geometry.dispose();
    const material = this.planeMesh.material as THREE.MeshBasicMaterial;
    material.dispose();
    this.planeEdges.geometry.dispose();
    this.planeEdgesMaterial.dispose();
    this.label.geometry.dispose();
    this.label.material.dispose();
    this.labelTexture.dispose();
  }
}
